import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Video, 
  User, 
  Briefcase, 
  Calendar, 
  ChevronLeft,
  Play,
  Download,
  FileVideo,
  Clock,
  HardDrive,
  FileText,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import { getVideoTestResponses, updateVideoTestNotes } from 'api/apiHelper';
import { VideoResponse } from 'types/video-test-types';

interface TestSessionInfo {
  id: number;
  test_token: string;
  status: string;
  applicant_name: string;
  applicant_email: string;
  job_title: string;
  started_at: string | null;
  completed_at: string | null;
  total_questions: number;
  reviewer_notes: string;
}

interface ReviewData {
  test_session: TestSessionInfo;
  video_responses: VideoResponse[];
}

const VideoTestReviewDetail: React.FC = () => {
  const { testToken } = useParams<{ testToken: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [savingNotes, setSavingNotes] = useState<boolean>(false);
  const [notesModified, setNotesModified] = useState<boolean>(false);

  useEffect(() => {
    if (testToken) {
      fetchResponses();
    }
  }, [testToken]);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const response = await getVideoTestResponses(testToken!);
      setReviewData(response.data);
      setNotes(response.data.test_session.reviewer_notes || '');
      setNotesModified(false);
    } catch (error: any) {
      console.error('Error fetching video responses:', error);
      toast.error('Failed to load video responses');
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    setNotesModified(value !== (reviewData?.test_session.reviewer_notes || ''));
  };

  const handleSaveNotes = async () => {
    if (!testToken) return;
    
    setSavingNotes(true);
    try {
      await updateVideoTestNotes(testToken, notes);
      toast.success('Notes saved successfully');
      setNotesModified(false);
      // Update local state
      if (reviewData) {
        setReviewData({
          ...reviewData,
          test_session: {
            ...reviewData.test_session,
            reviewer_notes: notes
          }
        });
      }
    } catch (error: any) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return <Backdrop message="Loading video responses..." />;
  }

  if (!reviewData) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">No data found</p>
      </div>
    );
  }

  const { test_session, video_responses } = reviewData;
  const currentVideo = video_responses[selectedVideo];

  return (
    <div className="p-6">
      {/* Back button
      <button
        onClick={() => navigate('/video-test-reviews')}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-brand-500 mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5 mr-1" />
        Back to Submissions
      </button> */}

      {/* Header Card */}
      <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <div className="flex items-center justify-center w-16 h-16 bg-brand-500/10 rounded-2xl mr-4">
              <User className="w-8 h-8 text-brand-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {test_session.applicant_name}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {test_session.applicant_email}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center bg-gray-50 dark:bg-navy-700 px-4 py-2 rounded-xl">
              <Briefcase className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-gray-700 dark:text-gray-300">{test_session.job_title}</span>
            </div>
            <div className="flex items-center bg-gray-50 dark:bg-navy-700 px-4 py-2 rounded-xl">
              <Calendar className="w-4 h-4 text-gray-400 mr-2" />
              <span className="text-gray-700 dark:text-gray-300">
                {formatDate(test_session.completed_at)}
              </span>
            </div>
            <div className="flex items-center bg-emerald-50 dark:bg-emerald-500/20 px-4 py-2 rounded-xl">
              <Video className="w-4 h-4 text-emerald-500 mr-2" />
              <span className="text-emerald-700 dark:text-emerald-400">
                {video_responses.length} Videos
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Video List Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Video Responses
            </h3>
            <div className="space-y-2">
              {video_responses.map((response, index) => (
                <button
                  key={response.id}
                  onClick={() => setSelectedVideo(index)}
                  className={`w-full text-left p-4 rounded-xl transition-all ${
                    selectedVideo === index
                      ? 'bg-brand-500 text-white'
                      : 'bg-gray-50 dark:bg-navy-700 hover:bg-gray-100 dark:hover:bg-navy-600'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-lg mr-3 ${
                      selectedVideo === index
                        ? 'bg-white/20'
                        : 'bg-brand-500/10'
                    }`}>
                      <Play className={`w-5 h-5 ${
                        selectedVideo === index ? 'text-white' : 'text-brand-500'
                      }`} />
                    </div>
                    <div>
                      <p className={`font-medium ${
                        selectedVideo === index ? 'text-white' : 'text-gray-900 dark:text-white'
                      }`}>
                        Question {response.question_number}
                      </p>
                      <p className={`text-xs ${
                        selectedVideo === index ? 'text-white/70' : 'text-gray-500'
                      }`}>
                        {formatFileSize(response.file_size_bytes)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Video Player */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-lg overflow-hidden">
            {currentVideo ? (
              <>
                {/* Video */}
                <div className="aspect-video bg-black">
                  <video
                    key={currentVideo.id}
                    src={currentVideo.video_file_url || currentVideo.video_file}
                    controls
                    className="w-full h-full object-contain"
                    controlsList="nodownload"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>

                {/* Video Info */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        Question {currentVideo.question_number}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-400">
                        {currentVideo.question_text}
                      </p>
                    </div>
                    <a
                      href={currentVideo.video_file_url || currentVideo.video_file}
                      download={`response_q${currentVideo.question_number}.webm`}
                      className="flex items-center px-4 py-2 bg-gray-100 dark:bg-navy-700 hover:bg-gray-200 dark:hover:bg-navy-600 rounded-lg text-gray-700 dark:text-gray-300 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </a>
                  </div>

                  {/* Metadata */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100 dark:border-navy-600">
                    <div className="flex items-center">
                      <FileVideo className="w-5 h-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">Format</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">WebM</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <HardDrive className="w-5 h-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">File Size</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatFileSize(currentVideo.file_size_bytes)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">Duration</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {currentVideo.duration_seconds 
                            ? `${Math.floor(currentVideo.duration_seconds / 60)}:${(currentVideo.duration_seconds % 60).toString().padStart(2, '0')}`
                            : 'Up to 2 min'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">Recorded</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {new Date(currentVideo.recorded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No video selected</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reviewer Notes Section */}
      <div className="mt-6 bg-white dark:bg-navy-800 rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-brand-500 mr-2" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Reviewer Notes</h3>
          </div>
          <button
            onClick={handleSaveNotes}
            disabled={savingNotes || !notesModified}
            className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              notesModified
                ? 'bg-brand-500 hover:bg-brand-600 text-white'
                : 'bg-gray-100 dark:bg-navy-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {savingNotes ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Notes
              </>
            )}
          </button>
        </div>
        <textarea
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          placeholder="Add your notes about this candidate's video responses here..."
          rows={4}
          className="w-full px-4 py-3 bg-gray-50 dark:bg-navy-700 border border-gray-200 dark:border-navy-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
        />
        {notesModified && (
          <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
            You have unsaved changes
          </p>
        )}
      </div>
    </div>
  );
};

export default VideoTestReviewDetail;

